import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, API_V2, fmtN, ziClass } from "../lib/appCommon";
import { formatAiText } from "../lib/utils";
import ErrorBoundary from "./ErrorBoundary";
import LiveMarketDashboard from "./LiveMarketDashboard";
import ZBucksDashboard from "./ZBucksDashboard";
import ZiChatExpanded from "./modals/ZiChatExpanded";
import BrokerSettingsPanel from "./BrokerSettingsPanel";

// These OCI-status/state/kill fetch calls were missing credentials:"include"
// entirely, so no auth cookie was ever sent cross-origin — all 401'd,
// including the kill switch (same duplicate pattern found in
// OCIControlCenter.js). CSRF token: login/auth-me responses hand it back in
// the JSON body, stashed in localStorage under this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

function IntelTab() {
  const [subTab, setSubTab] = useState("chat");
  const [scanResults, setScanResults] = useState([]);
  const [corrData, setCorrData] = useState(null);
  const [scanning, setScanning] = useState(false);

  // ── Simulate state ──────────────────────────────────────────────────────────
  const [simEquity, setSimEquity] = useState("10000");
  const [simTrades, setSimTrades] = useState([{ symbol: "TRADE1", probability_win: 0.55, win_return_pct: 80, loss_return_pct: -100 }]);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // ── Earnings Intel state ────────────────────────────────────────────────────
  const [earnSymbol, setEarnSymbol] = useState("AAPL");
  const [earnResult, setEarnResult] = useState(null);
  const [earnLoading, setEarnLoading] = useState(false);
  const [earnCalendar, setEarnCalendar] = useState(null);
  const [earnCalLoading, setEarnCalLoading] = useState(false);
  const [liveCrush, setLiveCrush] = useState(null);

  const loadEarningsCalendar = async (syms) => {
    setEarnCalLoading(true);
    try {
      const url = syms ? `${API}/earnings?symbols=${syms}` : `${API}/earnings`;
      const r = await axios.get(url);
      setEarnCalendar(r.data?.earnings || []);
    } catch {} finally { setEarnCalLoading(false); }
  };

  useEffect(() => { loadEarningsCalendar(); }, []);

  // ── OCI v2 state ────────────────────────────────────────────────────────────
  const [ociStatus, setOciStatus] = useState(null);
  const [ociExec, setOciExec] = useState(null);
  const [ociSettingState, setOciSettingState] = useState(false);
  const [ociKilling, setOciKilling] = useState(false);
  const [ociKillMsg, setOciKillMsg] = useState("");

  const OCI_STATES = ["OBSERVE","ASSISTED","ARMED","LIVE","DEFENSE","LOCKED"];
  const OCI_COLORS = {
    OBSERVE:  { bg:"rgba(56,189,248,0.12)",  color:"#38bdf8",  border:"rgba(56,189,248,0.3)"  },
    ASSISTED: { bg:"rgba(167,139,250,0.12)", color:"#a78bfa",  border:"rgba(167,139,250,0.3)" },
    ARMED:    { bg:"rgba(251,191,36,0.12)",  color:"#fbbf24",  border:"rgba(251,191,36,0.3)"  },
    LIVE:     { bg:"rgba(74,222,128,0.12)",  color:"#4ade80",  border:"rgba(74,222,128,0.3)"  },
    DEFENSE:  { bg:"rgba(248,113,113,0.12)", color:"#f87171",  border:"rgba(248,113,113,0.3)" },
    LOCKED:   { bg:"rgba(248,113,113,0.2)",  color:"#ef4444",  border:"rgba(239,68,68,0.5)"   },
  };

  useEffect(() => {
    if (subTab !== "oci") return;
    const tick = () => {
      Promise.all([
        fetch(`${API_V2}/oci/status`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()).catch(() => null),
        fetch(`${API_V2}/execution/status`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()).catch(() => null),
      ]).then(([s, e]) => { if (s) setOciStatus(s); if (e) setOciExec(e); });
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [subTab]);

  const ociSetState = async (s) => {
    setOciSettingState(true);
    try {
      const res = await fetch(`${API_V2}/oci/state`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", ...csrfHeaders() }, body: JSON.stringify({ state: s }) }).then(r => r.json());
      setOciStatus(res);
    } catch {
      toast.error("Failed to change OCI state — try again.");
    } finally { setOciSettingState(false); }
  };

  const ociKill = async () => {
    setOciKilling(true);
    try {
      await fetch(`${API_V2}/execution/kill`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", ...csrfHeaders() }, body: JSON.stringify({ reason: "Manual kill from Intel tab" }) });
      setOciKillMsg("Kill switch activated."); setTimeout(() => setOciKillMsg(""), 4000);
    } catch {
      toast.error("Kill switch request failed — execution state unchanged. Try again.");
    } finally { setOciKilling(false); }
  };

  const runSimulate = async () => {
    setSimLoading(true); setSimResult(null);
    try {
      const r = await axios.post(`${API_V2}/portfolio/simulate`, {
        starting_equity: parseFloat(simEquity) || 10000,
        trades: simTrades.map(t => ({ symbol: t.symbol, probability_win: parseFloat(t.probability_win), win_return_pct: parseFloat(t.win_return_pct), loss_return_pct: parseFloat(t.loss_return_pct) })),
        simulations: 1000,
      });
      setSimResult(r.data);
    } catch {
      toast.error("Simulation failed — try again.");
    } finally { setSimLoading(false); }
  };

  const addSimTrade = () => setSimTrades(prev => [...prev, { symbol: `TRADE${prev.length + 1}`, probability_win: 0.55, win_return_pct: 80, loss_return_pct: -100 }]);
  const updateSimTrade = (i, key, val) => setSimTrades(prev => prev.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  const removeSimTrade = (i) => setSimTrades(prev => prev.filter((_, idx) => idx !== i));

  const runEarnings = async () => {
    setEarnLoading(true); setEarnResult(null); setLiveCrush(null);
    const sym = earnSymbol.toUpperCase();
    try {
      const [ociRes, crushRes] = await Promise.allSettled([
        axios.post(`${API_V2}/analyze-symbol`, {
          symbol: sym, market_rows: [], account: {}, positions: [],
        }),
        axios.get(`${API}/iv/crush?symbol=${sym}`),
      ]);
      if (ociRes.status === "fulfilled") setEarnResult(ociRes.value.data);
      if (crushRes.status === "fulfilled") setLiveCrush(crushRes.value.data);
      if (ociRes.status === "rejected" && crushRes.status === "rejected") {
        toast.error(`Earnings analysis for ${sym} failed — try again.`);
      }
    } catch {
      toast.error(`Earnings analysis for ${sym} failed — try again.`);
    } finally { setEarnLoading(false); }
  };

  // Intel report state
  const [intelReport, setIntelReport] = useState("");
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelTs, setIntelTs] = useState("");


  const generateIntel = async () => {
    setIntelLoading(true);
    setIntelReport("");
    try {
      const [regimeRes, preloadRes] = await Promise.all([
        axios.get(`${API}/zi/regime?symbol=SPY`).catch(() => ({ data: {} })),
        axios.get(`${API}/preload`).catch(() => ({ data: {} })),
      ]);
      const preload = preloadRes.data || {};
      const opRaw = preload?.openPositions;
      const intelPositions = Array.isArray(opRaw) ? opRaw : (opRaw?.positions || []);
      const response = await fetch(`${API}/zi/intel/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "include",
        body: JSON.stringify({ regime: regimeRes.data, positions: intelPositions, portfolioContext: preload?.portfolio || null })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.chunk) {
                fullText += data.chunk;
                setIntelReport(fullText);
              }
              if (data.done) {
                setIntelReport(fullText);
                setIntelTs(data.generatedAt || "");
              }
              if (data.error) {
                setIntelReport(`Error: ${data.error}`);
              }
            } catch {}
          }
        }
      }
    } catch (e) {
      setIntelReport(`Error: ${e.message}`);
    } finally { setIntelLoading(false); }
  };

  const downloadPdf = async () => {
    const el = document.getElementById("intel-report-content");
    if (!el || !intelReport) return;
    const html2pdf = (await import("html2pdf.js")).default;
    html2pdf().set({
      margin: [12, 16],
      filename: `Ultra-ZI-AI-Intel-${new Date().toISOString().split("T")[0]}.pdf`,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(el).save();
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/zi/scan`, { tickers: ["SPY", "QQQ", "AAPL", "NVDA", "TSLA", "AAL", "AMZN", "MSFT"] });
      setScanResults(r.data?.tickers || []);
    } catch {
      toast.error("Scan failed — try again.");
    } finally { setScanning(false); }
  };

  const runCorr = async () => {
    try {
      const r = await axios.post(`${API}/zi/correlation`, { symbols: ["SPY", "QQQ", "AAPL", "NVDA", "TSLA", "AAL", "AMZN", "MSFT"] });
      setCorrData(r.data);
    } catch {
      toast.error("Correlation calculation failed — try again.");
    }
  };

  return (
    <div data-testid="intel-tab">
      <div className="stabs">
        {[
          { id: "chat", label: "AI Chat" },
          { id: "report", label: "Daily Briefing" },
          { id: "scan", label: "Market Scan" },
          { id: "corr", label: "Correlation" },
          { id: "live", label: "Live Market" },
          { id: "simulate", label: "Simulate" },
          { id: "earnings", label: "Earnings Intel" },
          { id: "oci", label: "OCI v2" },
          { id: "zbucks", label: "ZBucks" },
          { id: "broker", label: "Broker" },
        ].map(t => (
          <button key={t.id} className={`stab ${subTab === t.id ? "active" : ""}`} onClick={() => setSubTab(t.id)} data-testid={`intel-subtab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* AI Portfolio Chat — always mounted so context stays warm; hidden via CSS when tab is inactive */}
      <div style={{ display: subTab === "chat" ? "flex" : "none", flexDirection: "column" }}>
        <ErrorBoundary label="Chat is having trouble loading">
          <ZiChatExpanded portfolioContext={null} regime={null} />
        </ErrorBoundary>
      </div>

      {/* Daily Intel Report */}
      {subTab === "report" && (
        <div>
          <div className="row">
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Daily Intelligence Briefing</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>7-section AI briefing: regime, portfolio health, opportunities, risk, Bayesian updates, execution plan, watchlist</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {intelReport && !intelLoading && (
                <button className="btn" onClick={downloadPdf} data-testid="download-pdf" style={{ fontSize: 11 }}>
                  Download PDF
                </button>
              )}
              <button className="btn btn-blue" onClick={generateIntel} disabled={intelLoading} data-testid="gen-intel">
                {intelLoading ? "Streaming..." : "Generate Briefing"}
              </button>
            </div>
          </div>
          <div id="intel-report-content" style={{ background: "var(--layer1)", borderRadius: 8, padding: 14, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.8, color: "var(--muted)", minHeight: 200 }}
            data-testid="intel-report"
            dangerouslySetInnerHTML={{ __html: intelReport ? formatAiText(intelReport) + (intelLoading ? '<span class="stream-cursor"></span>' : '') : 'Click "Generate Briefing" to create your daily intelligence report.' }} />
          {intelTs && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 8 }}>Generated: {new Date(intelTs).toLocaleString()}</div>}
        </div>
      )}

      {/* Market Scan */}
      {subTab === "scan" && (
        <div>
          <div className="row"><span className="sh">Market Scan</span><button className="btn btn-blue" onClick={runScan} data-testid="run-market-scan">{scanning ? "Scanning..." : "Run Scan"}</button></div>
          {scanResults.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl">
                <thead><tr>
                  <th style={{ textAlign: "left" }}>Ticker</th><th>Price</th><th>Regime</th><th>RSI</th><th>Vol Ratio</th><th>EMA Aligned</th><th>Score</th><th>Ω Signal</th>
                </tr></thead>
                <tbody data-testid="scan-results">
                  {scanResults.map(t => (
                    <tr key={t.ticker}>
                      <td style={{ textAlign: "left", fontWeight: 500 }}>{t.ticker}</td>
                      <td>${fmtN(t.price, 2)}</td>
                      <td><span className={`tag ${t.regime === "bull" ? "t-bull" : t.regime === "bear" ? "t-bear" : "t-neu"}`}>{t.regime?.toUpperCase()}</span></td>
                      <td>{fmtN(t.rsi, 1)}</td>
                      <td>{fmtN(t.volRatio, 2)}x</td>
                      <td>{t.emaAligned ? "Yes" : "No"}</td>
                      <td><span className={`zi ${ziClass(t.scanScore)}`}>{t.scanScore}</span></td>
                      <td>{t.ziSignal?.bias ? <span className={`tag ${t.ziSignal.bias === "BULLISH" ? "t-bull" : t.ziSignal.bias === "BEARISH" ? "t-bear" : "t-neu"}`} style={{ fontSize: 8 }}>{t.ziSignal.bias}</span> : <span style={{ color: "var(--dim)" }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {scanResults.length === 0 && <div style={{ color: "var(--muted)", fontSize: 12 }}>Click Run Scan to analyze tickers.</div>}
        </div>
      )}

      {/* Correlation Matrix */}
      {subTab === "corr" && (
        <div>
          <div className="row"><span className="sh">Asset Correlation Matrix</span><button className="btn btn-blue" onClick={runCorr} data-testid="run-correlation">Compute Correlations</button></div>
          {corrData ? (
            <div style={{ overflowX: "auto" }}>
              <table className="corr-table">
                <thead><tr><th></th>{corrData.symbols.map(s => <th key={s}>{s}</th>)}</tr></thead>
                <tbody>
                  {corrData.symbols.map((s, i) => (
                    <tr key={s}>
                      <th style={{ textAlign: "left", fontWeight: 500, color: "var(--text)" }}>{s}</th>
                      {corrData.matrix[i].map((v, j) => (
                        <td key={j} style={{ background: v > 0.5 ? "rgba(74,222,128,0.15)" : v < -0.3 ? "rgba(248,113,113,0.15)" : "transparent", color: v === 1 ? "var(--blue)" : "var(--muted)" }}>
                          {v.toFixed(3)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: "var(--muted)", fontSize: 12 }}>Click Compute Correlations to build the matrix.</div>}
        </div>
      )}

      {subTab === "live" && <LiveMarketDashboard />}

      {/* ── Monte Carlo Portfolio Simulation ──────────────────────────────── */}
      {subTab === "simulate" && (
        <div>
          <div className="row" style={{ marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Portfolio Simulation</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>Monte Carlo — 1,000 simulations across your trade set. See avg outcome, best/worst case, and max drawdown.</div>
            </div>
            <button className="btn btn-blue" onClick={runSimulate} disabled={simLoading}>{simLoading ? "Simulating..." : "Run Simulation"}</button>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)" }}>Starting Equity</div>
              <input className="inp" value={simEquity} onChange={e => setSimEquity(e.target.value)} style={{ width: 110 }} placeholder="10000" />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>Trade Set</div>
            {simTrades.map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                <input className="inp" value={t.symbol} onChange={e => updateSimTrade(i, "symbol", e.target.value)} style={{ width: 80 }} placeholder="Symbol" />
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>P(Win)%</span>
                  <input className="inp" value={t.probability_win} onChange={e => updateSimTrade(i, "probability_win", e.target.value)} style={{ width: 60 }} placeholder="0.55" />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>Win%</span>
                  <input className="inp" value={t.win_return_pct} onChange={e => updateSimTrade(i, "win_return_pct", e.target.value)} style={{ width: 60 }} placeholder="80" />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>Loss%</span>
                  <input className="inp" value={t.loss_return_pct} onChange={e => updateSimTrade(i, "loss_return_pct", e.target.value)} style={{ width: 60 }} placeholder="-100" />
                </div>
                {simTrades.length > 1 && <button onClick={() => removeSimTrade(i)} style={{ fontSize: 10, color: "var(--red)", background: "none", border: "none", cursor: "pointer" }}>✕</button>}
              </div>
            ))}
            <button onClick={addSimTrade} className="btn" style={{ fontSize: 10, marginTop: 4 }}>+ Add Trade</button>
          </div>

          {simResult && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>Simulation Results — 1,000 runs</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
                {[
                  { label: "Avg Ending Equity", val: `$${fmtN(simResult.ending_equity_avg, 0)}`, color: "var(--text)" },
                  { label: "Best Case", val: `$${fmtN(simResult.best_case, 0)}`, color: "var(--green)" },
                  { label: "Worst Case", val: `$${fmtN(simResult.worst_case, 0)}`, color: "var(--red)" },
                  { label: "Max Drawdown", val: `${(simResult.max_drawdown * 100).toFixed(1)}%`, color: simResult.max_drawdown > 0.3 ? "var(--red)" : "var(--muted)" },
                  { label: "Win Rate", val: `${(simResult.win_rate * 100).toFixed(1)}%`, color: simResult.win_rate >= 0.5 ? "var(--green)" : "var(--red)" },
                  { label: "Starting Equity", val: `$${fmtN(simResult.starting_equity, 0)}`, color: "var(--muted)" },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>
              {simResult.ending_equity_avg > simResult.starting_equity ? (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: "rgba(74,222,128,0.06)", borderLeft: "3px solid var(--green)", fontSize: 11, color: "var(--text)" }}>
                  Average outcome is profitable. Return: <strong style={{ color: "var(--green)" }}>+{(((simResult.ending_equity_avg - simResult.starting_equity) / simResult.starting_equity) * 100).toFixed(1)}%</strong>
                </div>
              ) : (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: "rgba(248,113,113,0.06)", borderLeft: "3px solid var(--red)", fontSize: 11, color: "var(--text)" }}>
                  Average outcome is a loss. Expected return: <strong style={{ color: "var(--red)" }}>{(((simResult.ending_equity_avg - simResult.starting_equity) / simResult.starting_equity) * 100).toFixed(1)}%</strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Earnings Intelligence ─────────────────────────────────────────── */}
      {subTab === "earnings" && (
        <div>
          {/* Live Earnings Calendar */}
          <div className="card" style={{ marginBottom: 14, borderColor: "rgba(251,191,36,0.2)", background: "rgba(251,191,36,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div className="sh" style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                Upcoming Earnings Calendar
                <span className="tag t-neu" style={{ fontSize: 7 }}>DAILY CACHED</span>
              </div>
              <button className="btn" onClick={() => loadEarningsCalendar()} disabled={earnCalLoading} style={{ fontSize: 10 }}>
                {earnCalLoading ? "Loading..." : "Refresh"}
              </button>
            </div>
            {earnCalLoading && <div style={{ fontSize: 11, color: "var(--muted)" }}>Fetching earnings dates...</div>}
            {earnCalendar && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
                {earnCalendar.map((e, i) => {
                  const urgent = e.daysUntil != null && e.daysUntil <= 3;
                  const soon = e.daysUntil != null && e.daysUntil <= 7;
                  const dateColor = urgent ? "#f87171" : soon ? "#fbbf24" : "var(--muted)";
                  return (
                    <div key={i}
                      style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer2)", cursor: "pointer",
                        border: `1px solid ${urgent ? "rgba(248,113,113,0.35)" : soon ? "rgba(251,191,36,0.25)" : "var(--border)"}` }}
                      onClick={() => { setEarnSymbol(e.symbol); }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", marginBottom: 3 }}>{e.symbol}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: dateColor }}>
                        {e.earningsDate ? e.earningsDate : "No date"}
                      </div>
                      {e.daysUntil != null && (
                        <div style={{ fontSize: 9, color: dateColor, marginTop: 2 }}>
                          {e.daysUntil === 0 ? "Today!" : e.daysUntil < 0 ? `${Math.abs(e.daysUntil)}d ago` : `in ${e.daysUntil}d`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!earnCalLoading && (!earnCalendar || earnCalendar.length === 0) && (
              <div style={{ fontSize: 11, color: "var(--dim)" }}>No earnings data. Yahoo Finance may be throttling requests.</div>
            )}
          </div>

          {/* OCI Deep Analysis */}
          <div className="row" style={{ marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Earnings Intelligence</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>IV crush prediction · earnings phase · strategy decision · auto-entry triggers</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="inp" value={earnSymbol} onChange={e => setEarnSymbol(e.target.value.toUpperCase())} style={{ width: 80 }} placeholder="AAPL" />
              <button className="btn btn-blue" onClick={runEarnings} disabled={earnLoading}>{earnLoading ? "Analyzing..." : "Analyze"}</button>
            </div>
          </div>

          {earnResult && (() => {
            const ec = earnResult.earnings_context || {};
            const strategy = ec.strategy_decision || {};
            const ivCrush = ec.iv_crush || {};
            const playbook = ec.playbook || {};
            const trigger = ec.auto_entry_trigger || {};
            const actionColor = { NO_TRADE: "#f87171", WAIT_FOR_RELEASE: "#fbbf24", BUY_DEBIT_SPREAD: "#60a5fa", BUY_STRADDLE: "#a78bfa", POST_EVENT_DIRECTIONAL_CALL: "#4ade80", POST_EVENT_DIRECTIONAL_PUT: "#f87171", POST_EVENT_FADE: "#fbbf24", REDUCE_SIZE_ONLY: "#fbbf24", WATCH: "var(--muted)" };
            const ac = actionColor[strategy.action] || "var(--muted)";
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Strategy Decision */}
                <div className="card">
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Earnings Strategy</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: ac }}>{strategy.action?.replace(/_/g, " ")}</span>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "rgba(167,139,250,0.1)", color: "#a78bfa" }}>Phase: {strategy.phase?.replace(/_/g, " ")}</span>
                    {strategy.confidence != null && <span style={{ fontSize: 10, color: "var(--muted)" }}>Confidence: {(strategy.confidence * 100).toFixed(0)}%</span>}
                    {strategy.iv_crush_warning && <span style={{ fontSize: 9, fontWeight: 700, color: "#fbbf24", padding: "1px 6px", borderRadius: 3, background: "rgba(251,191,36,0.1)" }}>IV CRUSH WARNING</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text)", marginBottom: 6 }}>{strategy.reason}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>Structure: <strong style={{ color: "var(--text)" }}>{strategy.preferred_structure?.replace(/_/g, " ")}</strong></div>
                  {strategy.required_confirmations?.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {strategy.required_confirmations.map((c, i) => <span key={i} style={{ fontSize: 8, color: "#60a5fa", background: "rgba(96,165,250,0.08)", padding: "1px 5px", borderRadius: 3 }}>{c.replace(/_/g, " ")}</span>)}
                    </div>
                  )}
                  {strategy.blocked_reasons?.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {strategy.blocked_reasons.map((b, i) => <span key={i} style={{ fontSize: 8, color: "#f87171", background: "rgba(248,113,113,0.08)", padding: "1px 5px", borderRadius: 3 }}>{b.replace(/_/g, " ")}</span>)}
                    </div>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {/* IV Crush */}
                  <div className="card">
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>IV Crush Prediction</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: ivCrush.risk_level === "HIGH" ? "#f87171" : ivCrush.risk_level === "MEDIUM" ? "#fbbf24" : "#4ade80", marginBottom: 4 }}>
                      {ivCrush.predicted_crush_pct != null ? `${Number(ivCrush.predicted_crush_pct).toFixed(1)}%` : "—"}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 3, display: "inline-block", background: ivCrush.risk_level === "HIGH" ? "rgba(248,113,113,0.12)" : ivCrush.risk_level === "MEDIUM" ? "rgba(251,191,36,0.1)" : "rgba(74,222,128,0.1)", color: ivCrush.risk_level === "HIGH" ? "#f87171" : ivCrush.risk_level === "MEDIUM" ? "#fbbf24" : "#4ade80", marginBottom: 6 }}>{ivCrush.risk_level} RISK</div>
                    <div style={{ fontSize: 10, color: "var(--muted)" }}>{ivCrush.reason}</div>
                    {ivCrush.confidence != null && <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 4 }}>Confidence: {(ivCrush.confidence * 100).toFixed(0)}%</div>}
                    {/* Live options chain data from /iv/crush */}
                    {liveCrush && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>Live Options Chain</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 9 }}>
                          <div style={{ color: "var(--muted)" }}>IV Rank <strong style={{ color: "var(--text)" }}>{liveCrush.ivRank}%</strong></div>
                          <div style={{ color: "var(--muted)" }}>IV Pct <strong style={{ color: "var(--text)" }}>{liveCrush.ivPercentile}%</strong></div>
                          <div style={{ color: "var(--muted)" }}>Current IV <strong style={{ color: "var(--text)" }}>{liveCrush.currentIV}%</strong></div>
                          <div style={{ color: "var(--muted)" }}>Impl Move <strong style={{ color: "#fbbf24" }}>±{liveCrush.impliedMovePct?.toFixed(1)}%</strong></div>
                          <div style={{ color: "var(--muted)" }}>Hist Move <strong style={{ color: "var(--text)" }}>±{liveCrush.historicalMovePct?.toFixed(1)}%</strong></div>
                          <div style={{ color: "var(--muted)" }}>Live Crush <strong style={{ color: liveCrush.risk_level === "HIGH" ? "#f87171" : liveCrush.risk_level === "MEDIUM" ? "#fbbf24" : "#4ade80" }}>{liveCrush.predicted_crush_pct?.toFixed(1)}%</strong></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Auto Entry Trigger */}
                  <div className="card">
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Auto Entry Trigger</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: trigger.trigger ? "#4ade80" : "var(--muted)", marginBottom: 6 }}>
                      {trigger.trigger ? "TRIGGERED" : "NO TRIGGER"}
                    </div>
                    {trigger.trigger && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 10 }}>
                        <div style={{ color: "var(--text)" }}>Direction: <strong style={{ color: trigger.direction === "bullish" ? "#4ade80" : "#f87171" }}>{trigger.direction?.toUpperCase()}</strong></div>
                        <div style={{ color: "var(--muted)" }}>Entry: <strong>${fmtN(trigger.entry_price, 2)}</strong></div>
                        <div style={{ color: "#f87171" }}>Stop: <strong>${fmtN(trigger.stop_loss, 2)}</strong></div>
                        <div style={{ color: "#4ade80" }}>Target: <strong>${fmtN(trigger.take_profit, 2)}</strong></div>
                        <div style={{ color: "var(--dim)", marginTop: 3, fontSize: 9 }}>{trigger.reason}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Playbook */}
                {playbook.symbol && (
                  <div className="card">
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Historical Playbook</div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 10 }}>
                      <div><span style={{ color: "var(--muted)" }}>Beat Rate: </span><strong style={{ color: "var(--text)" }}>{playbook.beat_rate != null ? `${(playbook.beat_rate * 100).toFixed(0)}%` : "—"}</strong></div>
                      <div><span style={{ color: "var(--muted)" }}>Avg Move: </span><strong style={{ color: "var(--text)" }}>{playbook.avg_actual_move_1d_pct != null ? `${Number(playbook.avg_actual_move_1d_pct).toFixed(1)}%` : "—"}</strong></div>
                      <div><span style={{ color: "var(--muted)" }}>IV Crush Hist: </span><strong style={{ color: "var(--text)" }}>{playbook.avg_iv_crush_pct != null ? `${Number(playbook.avg_iv_crush_pct).toFixed(1)}%` : "—"}</strong></div>
                      <div><span style={{ color: "var(--muted)" }}>Confidence: </span><strong style={{ color: "var(--text)" }}>{playbook.confidence != null ? `${(playbook.confidence * 100).toFixed(0)}%` : "—"}</strong></div>
                    </div>
                    {playbook.preferred_structure && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>Preferred: <strong style={{ color: "var(--text)" }}>{playbook.preferred_structure.replace(/_/g, " ")}</strong></div>}
                  </div>
                )}
              </div>
            );
          })()}
          {!earnResult && !earnLoading && <div style={{ color: "var(--muted)", fontSize: 12 }}>Enter a symbol and click Analyze to run the full earnings intelligence pipeline.</div>}
          {earnLoading && <div style={{ color: "var(--muted)", fontSize: 12, padding: 20, textAlign: "center" }}><span className="spin" /> Running earnings intelligence pipeline...</div>}
        </div>
      )}

      {/* ── OCI v2 Control ────────────────────────────────────────────────── */}
      {subTab === "oci" && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>OCI v2 — Omega Core Intelligence</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 14 }}>Unified decision authority. Controls all trade approvals, earnings risk gating, Green Diamond policy, and EV sizing.</div>

          {/* State selector */}
          <div className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)" }}>Current State</div>
              {ociStatus?.state && (() => { const sc = OCI_COLORS[ociStatus.state] || OCI_COLORS.ASSISTED; return (
                <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 12px", borderRadius: 5, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{ociStatus.state}</span>
              ); })()}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {OCI_STATES.map(s => { const c = OCI_COLORS[s]; const active = ociStatus?.state === s; return (
                <button key={s} disabled={ociSettingState} onClick={() => ociSetState(s)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontWeight: 600, border: active ? `1px solid ${c.border}` : "1px solid var(--border)", background: active ? c.bg : "var(--layer2)", color: active ? c.color : "var(--muted)", opacity: ociSettingState ? 0.6 : 1 }}>{s}</button>
              ); })}
            </div>
            <div style={{ fontSize: 9, color: "var(--dim)" }}>OBSERVE: read-only · ASSISTED: suggestions · ARMED: queues, needs confirm · LIVE: full auto · DEFENSE: exits only · LOCKED: frozen</div>
          </div>

          {/* Last decision */}
          {ociStatus?.last_decision && (() => {
            const last = ociStatus.last_decision;
            const ev = last.unified_context?.ev_sizing;
            return (
              <div className="card" style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>Last Decision</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: last.action === "BUY" ? "var(--green)" : last.action === "SELL" ? "var(--red)" : last.action === "BLOCK" ? "#f87171" : "var(--muted)" }}>{last.action}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{last.symbol}</span>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>Confidence: {last.confidence != null ? (last.confidence * 100).toFixed(0) + "%" : "—"}</span>
                  <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: last.allowed_to_execute ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.1)", color: last.allowed_to_execute ? "#4ade80" : "#f87171" }}>{last.allowed_to_execute ? "EXECUTE ALLOWED" : "NEEDS CONFIRM"}</span>
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 6 }}>{last.reason}</div>
                {last.hierarchy_path?.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  {last.hierarchy_path.map((p, i) => <span key={i} style={{ fontSize: 8, background: "var(--layer2)", padding: "1px 5px", borderRadius: 3, color: "var(--dim)" }}>{p}</span>)}
                </div>}
                {last.warnings?.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  {last.warnings.map((w, i) => <span key={i} style={{ fontSize: 8, color: "#fbbf24", background: "rgba(251,191,36,0.08)", padding: "1px 5px", borderRadius: 3 }}>{w}</span>)}
                </div>}
                {ev && (
                  <div style={{ padding: "7px 10px", borderRadius: 5, background: "rgba(167,139,250,0.06)", borderLeft: "3px solid #a78bfa" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", marginBottom: 4 }}>◆ EV Position Sizing</div>
                    <div style={{ display: "flex", gap: 10, fontSize: 9, flexWrap: "wrap", color: "var(--text)" }}>
                      <span>Mode: <strong style={{ color: ev.mode === "BLOCK" ? "#f87171" : ev.mode === "AGGRESSIVE" ? "#4ade80" : "#a78bfa" }}>{ev.mode}</strong></span>
                      <span>Contracts: <strong>{ev.suggested_contracts}</strong></span>
                      <span>Notional: <strong>${(ev.suggested_notional || 0).toFixed(0)}</strong></span>
                      <span>Max Risk: <strong>${(ev.max_risk_dollars || 0).toFixed(0)}</strong></span>
                      <span>Edge Score: <strong>{(ev.edge_score || 0).toFixed(1)}</strong></span>
                    </div>
                    {ev.blocked_reason && <div style={{ fontSize: 8, color: "#f87171", marginTop: 3 }}>Blocked: {ev.blocked_reason}</div>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Execution + kill switch */}
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>Execution Status</div>
            {ociExec ? (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 10, color: "var(--muted)", marginBottom: 12 }}>
                <span>Mode: <strong style={{ color: "var(--text)" }}>{ociExec.mode || "—"}</strong></span>
                <span>Orders today: <strong style={{ color: "var(--text)" }}>{ociExec.orders_today ?? "—"}</strong> / {ociExec.max_orders_per_day ?? "—"}</span>
                {ociExec.kill_switch_active && <span style={{ color: "#f87171", fontWeight: 700 }}>KILL SWITCH ACTIVE</span>}
              </div>
            ) : <div style={{ fontSize: 10, color: "var(--dim)", marginBottom: 12 }}>Loading...</div>}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button disabled={ociKilling} onClick={ociKill} style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", borderRadius: 6, cursor: "pointer", border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>
                {ociKilling ? "Activating..." : "Kill Switch"}
              </button>
              {ociKillMsg && <span style={{ fontSize: 10, color: "#f87171" }}>{ociKillMsg}</span>}
            </div>
          </div>

          {(!ociStatus && !ociExec) && <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 12 }}>Connecting to OCI v2...</div>}
        </div>
      )}

      {/* ZBucks Learning Dashboard */}
      {subTab === "zbucks" && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>ZBucks Learning System</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>Omega builds a paper track record using ZBucks before any real-trade preview is unlocked. Every autopilot scan fires a learning cycle in the background.</div>
          </div>
          <ZBucksDashboard />
        </div>
      )}

      {/* Broker Settings */}
      {subTab === "broker" && <BrokerSettingsPanel />}
    </div>
  );
}

export default IntelTab;
